/**
 * The address search the shared `AddressAutocomplete` field asks (`/api/address/*`).
 * `app=crm` makes the server use the CRM's own Places key (Settings > Integrations >
 * Google Maps here), not Husly's. Names come back in the interface language.
 */
import { useMemo } from "react";
import type { AddressPick, AddressSource, AddressSuggestionItem } from "@wa/ui";
import { useI18n } from "../i18n/I18nProvider";
import { api } from "./api";

export function addressSource(lang: string, offNote?: string): AddressSource {
  return {
    suggest: async (q, session) => {
      const qs = new URLSearchParams({ q, lang, session, app: "crm" });
      const r = await api.get<{ suggestions: AddressSuggestionItem[]; reason?: string }>(`/address/suggest?${qs}`);
      // "no_key": the CRM has no Places key yet, so the field says why no list opens.
      return { suggestions: r.suggestions ?? [], off: r.reason === "no_key" };
    },
    details: async (placeId, session) => {
      const qs = new URLSearchParams({ lang, session, app: "crm" });
      const r = await api.get<{ address: AddressPick | null }>(`/address/details/${encodeURIComponent(placeId)}?${qs}`);
      return r.address ?? null;
    },
    offNote,
  };
}

/** The source for the reader's interface language. */
export function useAddressSource(): AddressSource {
  const { locale, t } = useI18n();
  return useMemo(() => addressSource(locale, t("common.addressSearchOff")), [locale, t]);
}
